import React, { useState } from 'react';
import { collection, getDocs, doc, updateDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';

interface FixLog {
  message: string;
  type: 'info' | 'success' | 'warning' | 'error';
  timestamp: Date;
}

export default function FixChannels() {
  const { user } = useAuth();
  const [logs, setLogs] = useState<FixLog[]>([]);
  const [isRunning, setIsRunning] = useState(false);
  const [stats, setStats] = useState({
    workspaces: 0,
    channels: 0,
    updated: 0
  });

  const addLog = (message: string, type: FixLog['type'] = 'info') => {
    setLogs(prev => [...prev, { message, type, timestamp: new Date() }]);
  };

  const fixChannelMembers = async () => {
    if (!user) {
      addLog('로그인이 필요합니다', 'error');
      return;
    }

    setIsRunning(true);
    setLogs([]);
    setStats({ workspaces: 0, channels: 0, updated: 0 });

    try {
      addLog('🚀 채널 멤버 수정 시작', 'info');

      // Get all workspaces
      const workspacesSnapshot = await getDocs(collection(db, 'workspaces'));
      const workspaceCount = workspacesSnapshot.size;
      setStats(prev => ({ ...prev, workspaces: workspaceCount }));
      addLog(`워크스페이스 ${workspaceCount}개 발견`, 'info');

      let totalChannels = 0;
      let updatedChannels = 0;

      for (const workspaceDoc of workspacesSnapshot.docs) {
        const workspaceId = workspaceDoc.id;
        const workspaceData = workspaceDoc.data();
        addLog(`📂 워크스페이스: ${workspaceData.name || workspaceId}`, 'info');

        // Get all members for this workspace
        const membersSnapshot = await getDocs(
          collection(db, `workspaces/${workspaceId}/members`)
        );
        const memberIds = membersSnapshot.docs.map(doc => doc.data().user_id);
        addLog(`   멤버 ${memberIds.length}명 발견`, 'info');

        // Get all channels for this workspace
        const channelsSnapshot = await getDocs(
          collection(db, `workspaces/${workspaceId}/channels`)
        );

        for (const channelDoc of channelsSnapshot.docs) {
          const channelData = channelDoc.data();
          const channelName = channelData.name || channelDoc.id;
          totalChannels++;

          // Update public channels with all workspace members
          if (channelData.type === 'public') {
            const channelRef = doc(db, `workspaces/${workspaceId}/channels`, channelDoc.id);
            await updateDoc(channelRef, {
              members: memberIds
            });
            updatedChannels++;
            addLog(`   ✅ ${channelName}: ${memberIds.length}명으로 업데이트`, 'success');
          }
          // For private channels, ensure owner is in members
          else if (channelData.type === 'private') {
            const currentMembers = channelData.members || [];
            if (!currentMembers.includes(channelData.owner_id)) {
              const channelRef = doc(db, `workspaces/${workspaceId}/channels`, channelDoc.id);
              await updateDoc(channelRef, {
                members: [...currentMembers, channelData.owner_id]
              });
              updatedChannels++;
              addLog(`   ✅ ${channelName}: owner 추가됨`, 'success');
            } else {
              addLog(`   ℹ️  ${channelName}: 이미 올바름`, 'info');
            }
          }
        }

        setStats(prev => ({ ...prev, channels: totalChannels, updated: updatedChannels }));
      }

      addLog(`🎉 완료! ${updatedChannels}/${totalChannels} 채널 업데이트됨`, 'success');
    } catch (error: any) {
      addLog(`❌ 오류 발생: ${error.message}`, 'error');
      console.error('Error fixing channels:', error);
    } finally {
      setIsRunning(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-6">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl shadow-2xl p-8 border border-white/20">
          <h1 className="text-4xl font-bold text-white mb-2 flex items-center gap-3">
            📊 채널 멤버 자동 수정
          </h1>
          <p className="text-slate-300 mb-8">
            모든 public 채널의 멤버 목록을 워크스페이스 멤버로 자동 설정합니다
          </p>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="bg-blue-500/20 backdrop-blur rounded-xl p-4 border border-blue-400/30">
              <h3 className="text-blue-200 text-sm font-medium mb-2">워크스페이스</h3>
              <div className="text-3xl font-bold text-white">{stats.workspaces}</div>
            </div>
            <div className="bg-purple-500/20 backdrop-blur rounded-xl p-4 border border-purple-400/30">
              <h3 className="text-purple-200 text-sm font-medium mb-2">총 채널</h3>
              <div className="text-3xl font-bold text-white">{stats.channels}</div>
            </div>
            <div className="bg-green-500/20 backdrop-blur rounded-xl p-4 border border-green-400/30">
              <h3 className="text-green-200 text-sm font-medium mb-2">수정된 채널</h3>
              <div className="text-3xl font-bold text-white">{stats.updated}</div>
            </div>
          </div>

          {/* Action Button */}
          <button
            onClick={fixChannelMembers}
            disabled={isRunning || !user}
            className="w-full bg-gradient-to-r from-blue-500 to-purple-600 text-white font-semibold py-4 px-6 rounded-xl shadow-lg hover:shadow-xl transform hover:scale-[1.02] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none mb-6"
          >
            {isRunning ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                처리 중...
              </span>
            ) : (
              '채널 멤버 수정 시작'
            )}
          </button>

          {/* Logs */}
          <div className="bg-slate-900/50 backdrop-blur rounded-xl p-4 max-h-96 overflow-y-auto border border-slate-700">
            {logs.length === 0 ? (
              <p className="text-slate-400 text-center py-8">
                버튼을 클릭하여 시작하세요
              </p>
            ) : (
              <div className="space-y-2">
                {logs.map((log, index) => (
                  <div
                    key={index}
                    className={`p-3 rounded-lg ${
                      log.type === 'success'
                        ? 'bg-green-500/10 text-green-300 border border-green-500/30'
                        : log.type === 'error'
                        ? 'bg-red-500/10 text-red-300 border border-red-500/30'
                        : log.type === 'warning'
                        ? 'bg-yellow-500/10 text-yellow-300 border border-yellow-500/30'
                        : 'bg-blue-500/10 text-blue-300 border border-blue-500/30'
                    }`}
                  >
                    <span className="text-xs opacity-70 mr-2">
                      {log.timestamp.toLocaleTimeString('ko-KR')}
                    </span>
                    {log.message}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
